import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings, Save, RefreshCw, 
  CreditCard, Gift, Package,
  Plus, Trash2, Edit3, X, Clock, Shield, DollarSign
} from 'lucide-react';
import { adminAPI } from '../../api';
import toast from 'react-hot-toast';
import { formatCredits } from '../../utils/credits';

// Tab configuration
const TABS = [
  { id: 'general', label: 'General Settings', icon: Settings },
  { id: 'plans', label: 'Subscription Plans', icon: Package }
];

const CONFIG_ICONS = {
  new_user_signup_credits: CreditCard,
  trial_validity_days: Clock,
  trial_enabled: Shield,
  referral_bonus_credits: Gift,
  topup_packs: DollarSign
};

const CONFIG_LABELS = {
  new_user_signup_credits: 'Trial Usage Balance (₹)',
  trial_validity_days: 'Trial Validity (Days)',
  trial_enabled: 'Free Trial Enabled',
  referral_bonus_credits: 'Referral Bonus (₹)',
  topup_packs: 'Top-up Packs Config'
};

// ==================== General Settings Tab ====================
const GeneralSettingsTab = () => {
  const [configs, setConfigs] = useState({});
  const [schema, setSchema] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [editValues, setEditValues] = useState({});

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSystemConfig();
      setConfigs(response.data.configs || {});
      setSchema(response.data.schema || {});
      
      const initialValues = {};
      Object.entries(response.data.configs || {}).forEach(([key, config]) => {
        initialValues[key] = config.value;
      });
      setEditValues(initialValues);
    } catch (err) {
      toast.error('Failed to load system configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfigs(); }, []);

  const handleSave = async (configKey) => {
    const value = editValues[configKey];
    try {
      setSaving(prev => ({ ...prev, [configKey]: true }));
      await adminAPI.updateSystemConfig(configKey, { config_value: value });
      toast.success(`${CONFIG_LABELS[configKey] || configKey} updated successfully`);
      await fetchConfigs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(prev => ({ ...prev, [configKey]: false }));
    }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><RefreshCw className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="space-y-4">
      {Object.entries(configs).map(([key, config]) => {
        const Icon = CONFIG_ICONS[key] || Settings;
        const label = CONFIG_LABELS[key] || key;
        const schemaInfo = schema[key] || {};
        const changed = editValues[key] !== config.value;

        return (
          <div key={key} className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                  <Icon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-sm sm:text-base">{label}</h3>
                  <p className="text-xs sm:text-sm text-slate-500">{config.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={editValues[key] ?? ''}
                  onChange={(e) => setEditValues(prev => ({ ...prev, [key]: e.target.value }))}
                  min={schemaInfo.min}
                  max={schemaInfo.max}
                  className="w-24 sm:w-28 px-3 py-2 border rounded-lg text-right font-mono text-sm"
                />
                <button
                  onClick={() => handleSave(key)}
                  disabled={saving[key] || !changed}
                  className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-sm ${changed ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-100 text-slate-400'}`}
                >
                  {saving[key] ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ==================== Subscription Plans Tab ====================
const SubscriptionPlansTab = () => {
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSubscriptionPlans();
      setPlans(response.data.plans || {});
    } catch (err) {
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const handleEdit = (planId) => {
    setEditingPlan(planId);
    setEditValues(plans[planId] || {});
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await adminAPI.updateSubscriptionPlan(editingPlan, editValues);
      toast.success('Plan updated successfully');
      setEditingPlan(null);
      await fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update plan');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPlan = async () => {
    try {
      setSaving(true);
      await adminAPI.createSubscriptionPlan(editValues);
      toast.success('Plan created successfully');
      setShowAddForm(false);
      setEditValues({});
      await fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm(`Are you sure you want to deactivate the "${planId}" plan?`)) return;
    try {
      await adminAPI.deleteSubscriptionPlan(planId);
      toast.success('Plan deactivated');
      await fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete plan');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><RefreshCw className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  const sortedPlans = Object.entries(plans).sort((a, b) => (a[1].sort_order || 99) - (b[1].sort_order || 99));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-slate-600">Manage subscription plan pricing, credits, and API costs</p>
        <button
          onClick={() => { setShowAddForm(true); setEditValues({ plan_id: '', name: '', tryon_cost: 20, moments_cost: 5, price_monthly: 0, monthly_credits: 0 }); }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
        >
          <Plus className="w-4 h-4" />
          Add Plan
        </button>
      </div>

      {/* Plans Grid */}
      <div className="grid gap-4">
        {sortedPlans.map(([planId, plan]) => (
          <motion.div
            key={planId}
            layout
            className={`bg-white rounded-xl border p-5 ${!plan.is_active ? 'opacity-50' : ''} ${plan.popular ? 'border-blue-300 ring-2 ring-blue-100' : 'border-slate-200'}`}
          >
            {editingPlan === planId ? (
              // Edit Mode
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-lg text-slate-900">Edit: {plan.name}</h3>
                  <button onClick={() => setEditingPlan(null)} className="text-slate-400 hover:text-slate-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Name</label>
                    <input
                      type="text"
                      value={editValues.name || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Monthly Price (₹)</label>
                    <input
                      type="number"
                      value={editValues.price_monthly ?? ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, price_monthly: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Intro Price (₹)</label>
                    <input
                      type="number"
                      value={editValues.price_monthly_intro ?? ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, price_monthly_intro: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Monthly Credits</label>
                    <input
                      type="number"
                      value={editValues.monthly_credits ?? ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, monthly_credits: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Intro Credits</label>
                    <input
                      type="number"
                      value={editValues.monthly_credits_intro ?? ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, monthly_credits_intro: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">TryOn Cost (credits)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editValues.tryon_cost ?? ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, tryon_cost: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Moments Cost (credits)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editValues.moments_cost ?? ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, moments_cost: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Max SKUs</label>
                    <input
                      type="number"
                      value={editValues.max_skus ?? ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, max_skus: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      placeholder="-1 for unlimited"
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-2">
                  <button onClick={() => setEditingPlan(null)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 text-sm"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-bold text-lg text-slate-900">{plan.name}</h3>
                    {plan.popular && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Popular</span>}
                    {!plan.is_active && <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Inactive</span>}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{plan.tagline}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-4">
                    <div>
                      <p className="text-xs text-slate-500">Price</p>
                      <p className="font-semibold text-sm">₹{plan.price_monthly?.toLocaleString() || 0}/mo</p>
                      {plan.price_monthly_intro !== plan.price_monthly && (
                        <p className="text-xs text-emerald-600">Intro: ₹{plan.price_monthly_intro?.toLocaleString()}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Monthly Credits</p>
                      <p className="font-semibold text-sm">{formatCredits(plan.monthly_credits)}</p>
                      {plan.monthly_credits_intro !== plan.monthly_credits && (
                        <p className="text-xs text-emerald-600">Intro: {formatCredits(plan.monthly_credits_intro)}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">TryOn Cost</p>
                      <p className="font-semibold text-sm">{formatCredits(plan.tryon_cost)} cr</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Moments Cost</p>
                      <p className="font-semibold text-sm">{formatCredits(plan.moments_cost)} cr</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:ml-4">
                  <button
                    onClick={() => handleEdit(planId)}
                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  {planId !== 'starter' && (
                    <button
                      onClick={() => handleDelete(planId)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Add Plan Modal */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl p-6 max-w-lg w-full"
            >
              <h3 className="font-bold text-lg mb-4">Add New Subscription Plan</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Plan ID (lowercase)</label>
                  <input
                    type="text"
                    value={editValues.plan_id || ''}
                    onChange={(e) => setEditValues(prev => ({ ...prev, plan_id: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., premium"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Display Name</label>
                  <input
                    type="text"
                    value={editValues.name || ''}
                    onChange={(e) => setEditValues(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Monthly Price (₹)</label>
                  <input
                    type="number"
                    value={editValues.price_monthly ?? ''}
                    onChange={(e) => setEditValues(prev => ({ ...prev, price_monthly: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Monthly Credits</label>
                  <input
                    type="number"
                    value={editValues.monthly_credits ?? ''}
                    onChange={(e) => setEditValues(prev => ({ ...prev, monthly_credits: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">TryOn Cost</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editValues.tryon_cost ?? ''}
                    onChange={(e) => setEditValues(prev => ({ ...prev, tryon_cost: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Moments Cost</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editValues.moments_cost ?? ''}
                    onChange={(e) => setEditValues(prev => ({ ...prev, moments_cost: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button onClick={() => setShowAddForm(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">
                  Cancel
                </button>
                <button
                  onClick={handleAddPlan}
                  disabled={saving || !editValues.plan_id || !editValues.name}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Create Plan
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== Main Component ====================
const SystemConfig = () => {
  const [activeTab, setActiveTab] = useState('general');

  return (
    <div className="space-y-4 sm:space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="min-w-0">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />
          <span className="truncate">System Configuration</span>
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Manage system settings and subscription plans
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 overflow-x-auto">
        <nav className="flex gap-1 min-w-max">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 border-b-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'general' && <GeneralSettingsTab />}
        {activeTab === 'plans' && <SubscriptionPlansTab />}
      </div>
    </div>
  );
};

export default SystemConfig;
